import "react-day-picker/style.css";

import { Button } from "@admin/components/atoms/Button";
import useClickOutside from "@admin/hooks/useClickOutside";
import { useLanguageStore } from "@enos-monorepo/lib";
import { da, enUS, tr } from "date-fns/locale";
import { useRef, useState } from "react";
import { DayPicker as ReactDayPicker, Locale, Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";
export interface DatePickerProps {
  value: Date;
  onChange: (value: Date) => void;
  disabledDays?: Matcher | Matcher[];
}

export function DatePicker(props: DatePickerProps) {
  const { i18n, t } = useTranslation();
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const locales: Record<string, Locale> = {
    en: enUS,
    da: da,
    tr: tr,
  };
  const { language } = useLanguageStore();
  const mappedLanguage = locales[language ?? ""] || enUS;
  useClickOutside(ref, () => setShow(false));

  const formatDate = (date: Date) =>
    Intl.DateTimeFormat(i18n.language, {
      month: "short",
      day: "numeric",
      year: "numeric",
      weekday: "short",
    }).format(date);

  const formatTime = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handleTimeChange = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    const updated = new Date(props.value);
    updated.setHours(hours, minutes);
    props.onChange(updated);
  };

  return (
    <div className="relative dropdown">
      <Button
        size="md"
        variant="light"
        onClick={() => setShow((prev) => !prev)}
        className="dropdown-toggle min-w-64 select"
        type="button"
      >
        <span>
          {formatDate(props.value)} - {formatTime(props.value)}
        </span>
      </Button>

      {show && (
        <div
          ref={ref}
          className="card absolute right-0 top-full z-30 bg-white p-4 mt-2 shadow"
        >
          <ReactDayPicker
            locale={mappedLanguage}
            classNames={{
              day: "text-sm",
              focused: "bg-blue-100",
              selected: "bg-blue-100 text-blue-500",
              outside: "text-gray-300",
              disabled: "text-gray-300",
              day_button: "text-sm font-semibold rounded-full w-8 h-6",
              today: "text-blue-500",
              weekday: "text-sm font-semibold text-gray-700",
              chevron: "text-blue-500",
            }}
            showOutsideDays
            disabled={props.disabledDays}
            selected={props.value}
            mode="single"
            onDayClick={(day) => {
              const newDate = new Date(day);
              newDate.setHours(
                props.value.getHours(),
                props.value.getMinutes(),
              );
              props.onChange(newDate);
              setShow(false);
            }}
          />

          <div className="mt-4">
            <label
              htmlFor="timeInput"
              className="block mb-1 text-sm font-medium text-gray-700"
            >
              {t("shift.Select Hours")}
            </label>
            <input
              id="timeInput"
              type="time"
              value={formatTime(props.value)}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
}
